<script lang="ts">
  import { onMount } from 'svelte';
  import { signup, authState } from '../../lib/auth.svelte';
  import { db } from '../../lib/firebase';
  import { doc, setDoc, getDoc } from 'firebase/firestore';
  import { getQueryParams } from '../../lib/router.svelte';
  import { validateABN, formatABN } from '../../lib/abn';
  import {
    createSubscriptionCheckout,
    PLAN_PRICE_IDS,
  } from '../../lib/stripe';
  import { queueWelcomeEmail } from '../../lib/api';

  // Step components
  import {
    StepIndicator,
    BusinessDetailsStep,
    PlanSelectionStep,
    PaymentStep,
    type PlanId,
    type Cadence,
    type Plans,
  } from './components';

  // Current step (1, 2, or 3)
  let currentStep = $state(1);
  let checkoutStatus = $state<'success' | 'cancelled' | null>(null);

  // Stage 1 form data
  let firstName = $state('');
  let lastName = $state('');
  let email = $state('');
  let phone = $state('');
  let companyName = $state('');
  let abn = $state('');
  let industryType = $state('');
  let companySize = $state('');
  let referralSource = $state('');

  // Stage 2 form data
  let selectedPlan = $state<PlanId>('core');
  let cadence = $state<Cadence>('monthly');
  let acceptTerms = $state(false);

  // Form state
  let isSubmitting = $state(false);
  let errorMessage = $state('');
  let abnWarning = $state('');
  let showAlreadySignedUp = $state(false);

  // Dropdown options
  const industryTypes = [
    { value: '', label: 'Select Industry' },
    { value: 'ecommerce', label: 'E-commerce' },
    { value: 'retail', label: 'Retail' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'wholesale', label: 'Wholesale/Distribution' },
    { value: 'logistics', label: 'Logistics/3PL' },
    { value: 'other', label: 'Other' },
  ];

  const companySizes = [
    { value: '', label: 'Select Company Size' },
    { value: '1-5', label: '1-5 employees' },
    { value: '6-20', label: '6-20 employees' },
    { value: '21-50', label: '21-50 employees' },
    { value: '51-200', label: '51-200 employees' },
    { value: '200+', label: '200+ employees' },
  ];

  const referralSources = [
    { value: '', label: 'How did you hear about us?' },
    { value: 'google', label: 'Google Search' },
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'referral', label: 'Referral' },
    { value: 'event', label: 'Trade Show/Event' },
    { value: 'other', label: 'Other' },
  ];

  // Plan data
  const plans: Plans = {
    core: {
      name: 'Core',
      headline: 'Operational essentials',
      monthly: '$92',
      annual: '$83',
      summary: 'Orders, inventory, and contacts with 50 order limit.',
    },
    more: {
      name: 'More',
      headline: 'Growing warehouses',
      monthly: '$195',
      annual: '$176',
      summary: 'Everything in Core plus warehouse management, 500 order limit.',
    },
    complete: {
      name: 'Complete',
      headline: 'Full-stack suite',
      monthly: '$320',
      annual: '$288',
      summary: 'All modules with 1,000 order limit.',
    },
  };

  // Check for URL params on mount
  onMount(async () => {
    const params = getQueryParams();

    // Pre-select plan if specified
    const planParam = params.plan as PlanId | undefined;
    if (planParam && ['core', 'more', 'complete'].includes(planParam)) {
      selectedPlan = planParam;
    }

    // Check for checkout result
    if (params.checkout === 'success' || params.checkout === 'cancelled') {
      checkoutStatus = params.checkout;

      // Queue welcome email on successful checkout
      if (params.checkout === 'success' && authState.user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', authState.user.uid));
          const userData = userDoc.exists() ? userDoc.data() : {};

          const result = await queueWelcomeEmail({
            email: authState.user.email || '',
            firstName: userData.firstName || authState.user.displayName?.split(' ')[0],
            companyName: userData.companyName || 'EQUOS',
            source: 'signup',
          });
          if (!result.success) {
            console.error('Failed to queue welcome email:', result.error);
          }
        } catch (err) {
          console.error('Error queueing welcome email:', err);
        }
      }
      return;
    }

    // If user is already authenticated, pre-fill data and skip to appropriate step
    if (authState.isAuthenticated && authState.user) {
      try {
        const userDoc = await getDoc(doc(db, 'users', authState.user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          email = userData.email || authState.user.email || '';
          firstName = userData.firstName || '';
          lastName = userData.lastName || '';
          phone = userData.phone || '';
          companyName = userData.companyName || '';
          abn = userData.abn || '';
          industryType = userData.industryType || '';
          companySize = userData.companySize || '';
          referralSource = userData.referralSource || '';

          if (userData.selectedPlan) {
            selectedPlan = userData.selectedPlan;
          }
          if (userData.billingCadence) {
            cadence = userData.billingCadence;
          }

          if (userData.firstName && userData.companyName) {
            currentStep = 3;
          } else {
            currentStep = 2;
          }
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        currentStep = 2;
      }
    }
  });

  // ABN input handler
  function handleABNInput(event: Event) {
    const input = event.target as HTMLInputElement;
    abn = formatABN(input.value);
    abnWarning = abn.trim() && !validateABN(abn)
      ? 'This doesn\'t look like a valid 11-digit ABN'
      : '';
  }

  // Field change handler for BusinessDetailsStep
  function handleFieldChange(field: string, value: string) {
    switch (field) {
      case 'firstName': firstName = value; break;
      case 'lastName': lastName = value; break;
      case 'email': email = value; break;
      case 'phone': phone = value; break;
      case 'companyName': companyName = value; break;
      case 'industryType': industryType = value; break;
      case 'companySize': companySize = value; break;
      case 'referralSource': referralSource = value; break;
    }
  }

  // Stage 1 validation
  function validateStage1(): string | null {
    if (!firstName.trim()) return 'First name is required';
    if (!lastName.trim()) return 'Last name is required';
    if (!email.trim()) return 'Email is required';
    if (!email.includes('@')) return 'Please enter a valid email';
    if (!phone.trim()) return 'Phone number is required';
    if (!companyName.trim()) return 'Company name is required';
    return null;
  }

  // Stage 1 submit
  function submitStage1(e: Event) {
    e.preventDefault();
    const validationError = validateStage1();
    if (validationError) {
      errorMessage = validationError;
      return;
    }
    errorMessage = '';
    currentStep = 2;
  }

  // Stage 2 validation
  function validateStage2(): string | null {
    if (!acceptTerms) return 'Please accept the billing terms';
    return null;
  }

  // Stage 2 submit
  async function submitStage2(e: Event) {
    e.preventDefault();

    const validationError = validateStage2();
    if (validationError) {
      errorMessage = validationError;
      return;
    }

    if (authState.isAuthenticated) {
      currentStep = 3;
      errorMessage = '';
      return;
    }

    const stage1Error = validateStage1();
    if (stage1Error) {
      errorMessage = stage1Error + ' - Please go back and complete your details.';
      return;
    }

    isSubmitting = true;
    errorMessage = '';

    try {
      const randomPassword = crypto.randomUUID() + crypto.randomUUID();
      const result = await signup(email, randomPassword);

      if (!result.success) {
        if (result.errorCode === 'auth/email-already-in-use') {
          showAlreadySignedUp = true;
          return;
        }
        throw new Error(result.error || 'Failed to create account');
      }

      const user = result.user;
      if (!user) throw new Error('No user returned from signup');

      const userData = {
        email: email.toLowerCase().trim(),
        displayName: `${firstName.trim()} ${lastName.trim()}`,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        companyName: companyName.trim(),
        abn: abn.replace(/\s/g, ''),
        industryType,
        companySize,
        referralSource,
        selectedPlan,
        billingCadence: cadence,
        role: 'accountAdmin',
        userType: 'account_admin',
        isActive: true,
        approvalStatus: 'approved',
        claimsSet: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        source: 'signup-flow',
      };

      await setDoc(doc(db, 'users', user.uid), userData, { merge: true });
      currentStep = 3;
    } catch (error) {
      errorMessage = error instanceof Error ? error.message : 'Something went wrong. Please try again.';
      console.error('Account creation error:', error);
    } finally {
      isSubmitting = false;
    }
  }

  // Stage 3 submit with payment
  async function submitStage3WithPayment(e: Event) {
    e.preventDefault();

    if (!authState.user) {
      errorMessage = 'You must be logged in to complete checkout';
      return;
    }

    isSubmitting = true;
    errorMessage = '';

    try {
      const lineItems: Array<{ price: string; quantity?: number }> = [];
      const planPriceId = PLAN_PRICE_IDS[selectedPlan][cadence];
      if (!planPriceId) throw new Error('Invalid plan or cadence selected');
      lineItems.push({ price: planPriceId });

      const successUrl = `${window.location.origin}${window.location.pathname}#signup?checkout=success`;
      const cancelUrl = `${window.location.origin}${window.location.pathname}#signup?checkout=cancelled`;

      const checkoutUrl = await createSubscriptionCheckout(
        authState.user.uid,
        lineItems,
        successUrl,
        cancelUrl,
        { plan: selectedPlan, cadence, company: companyName, addOns: '' }
      );

      window.location.href = checkoutUrl;
    } catch (error) {
      errorMessage = error instanceof Error ? error.message : 'Unexpected error. Please try again.';
      console.error('Stage 3 payment error:', error);
    } finally {
      isSubmitting = false;
    }
  }

  // Skip payment and start trial
  async function skipPaymentStartTrial() {
    if (!authState.user) {
      errorMessage = 'You must be logged in to start trial';
      return;
    }

    isSubmitting = true;
    errorMessage = '';

    try {
      await setDoc(doc(db, 'users', authState.user.uid), {
        subscriptionStatus: 'trial',
        selectedPlan,
        billingCadence: cadence,
        trialStartedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }, { merge: true });

      await setDoc(doc(db, 'signupRequests', authState.user.uid), {
        userId: authState.user.uid,
        email: authState.user.email,
        selectedPlan,
        cadence,
        status: 'trial_started',
        trialStartedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }, { merge: true });

      const userDoc = await getDoc(doc(db, 'users', authState.user.uid));
      const userData = userDoc.exists() ? userDoc.data() : {};

      await queueWelcomeEmail({
        email: authState.user.email || '',
        firstName: userData.firstName || authState.user.displayName?.split(' ')[0],
        companyName: userData.companyName || 'EQUOS',
        source: 'signup',
      });

      checkoutStatus = 'success';
    } catch (error) {
      errorMessage = error instanceof Error ? error.message : 'Unexpected error. Please try again.';
      console.error('Skip payment error:', error);
    } finally {
      isSubmitting = false;
    }
  }

  // Navigation handlers
  function goBack() {
    if (currentStep > 1) currentStep = currentStep - 1;
    errorMessage = '';
  }

  function goToStep(step: number) {
    if (step === 3 && !authState.isAuthenticated) return;
    currentStep = step;
    errorMessage = '';
  }

  function goToDashboard() {
    window.location.hash = 'dashboard';
  }

  function goToLogin() {
    window.location.hash = 'login';
  }

  function retryCheckout() {
    checkoutStatus = null;
    currentStep = 3;
  }
</script>

<div class="signup-container">
  {#if checkoutStatus === 'success'}
    <div class="result-card success">
      <div class="result-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke-linecap="round" stroke-linejoin="round" />
          <polyline points="22,4 12,14.01 9,11.01" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </div>
      <h1>Welcome to EQUOS!</h1>
      <p>Your subscription has been activated successfully. Your 14-day free trial starts now.</p>
      <p class="next-steps">Check your email for login instructions and getting started guides.</p>
      <button class="btn-primary" onclick={goToDashboard}>Go to Dashboard</button>
    </div>
  {:else if checkoutStatus === 'cancelled'}
    <div class="result-card cancelled">
      <div class="result-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" stroke-linecap="round" />
          <line x1="9" y1="9" x2="15" y2="15" stroke-linecap="round" />
        </svg>
      </div>
      <h1>Checkout Cancelled</h1>
      <p>No worries! Your account has been created. You can complete your subscription whenever you're ready.</p>
      <button class="btn-primary" onclick={retryCheckout}>Try Again</button>
      <button class="btn-secondary" onclick={goToDashboard}>Go to Dashboard</button>
    </div>
  {:else}
    <div class="signup-shell">
      <header class="signup-header">
        <p class="eyebrow">Sign Up</p>
        <h1>Start your free 14-day trial</h1>
        <p class="lede">No credit card required. Get full access to EQUOS and see how it transforms your operations.</p>
      </header>

      <StepIndicator
        {currentStep}
        isAuthenticated={authState.isAuthenticated}
        onStepClick={goToStep}
      />

      {#if errorMessage}
        <div class="error-banner">
          <span>!</span>
          {errorMessage}
        </div>
      {/if}

      {#if currentStep === 1}
        <BusinessDetailsStep
          {firstName}
          {lastName}
          {email}
          {phone}
          {companyName}
          {abn}
          {abnWarning}
          {industryType}
          {companySize}
          {referralSource}
          {isSubmitting}
          isAuthenticated={authState.isAuthenticated}
          {industryTypes}
          {companySizes}
          {referralSources}
          onSubmit={submitStage1}
          onABNInput={handleABNInput}
          onFieldChange={handleFieldChange}
        />
      {:else if currentStep === 2}
        <PlanSelectionStep
          {selectedPlan}
          {cadence}
          {acceptTerms}
          {isSubmitting}
          {plans}
          onSubmit={submitStage2}
          onBack={goBack}
          onPlanSelect={(p) => selectedPlan = p}
          onCadenceSelect={(c) => cadence = c}
          onTermsChange={(t) => acceptTerms = t}
        />
      {:else if currentStep === 3}
        <PaymentStep
          {selectedPlan}
          {cadence}
          {plans}
          {isSubmitting}
          {errorMessage}
          onPaymentSubmit={submitStage3WithPayment}
          onSkipPayment={skipPaymentStartTrial}
          onBack={goBack}
        />
      {/if}
    </div>
  {/if}
</div>

{#if showAlreadySignedUp}
  <div
    class="dialog-overlay"
    onclick={() => (showAlreadySignedUp = false)}
    onkeydown={(e) => e.key === 'Escape' && (showAlreadySignedUp = false)}
    role="dialog"
    aria-modal="true"
    tabindex="-1"
  >
    <div class="dialog-card" onclick={(e) => e.stopPropagation()} role="document">
      <div class="dialog-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" stroke-linecap="round" />
          <line x1="12" y1="16" x2="12.01" y2="16" stroke-linecap="round" />
        </svg>
      </div>
      <h2>Whoops! You've already signed up</h2>
      <p>An account with this email already exists. Would you like to go to the login screen?</p>
      <div class="dialog-actions">
        <button class="btn-secondary" onclick={() => (showAlreadySignedUp = false)}>Stay Here</button>
        <button class="btn-primary" onclick={goToLogin}>Go to Login</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .signup-container {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #4A7DFF 0%, #6B5CE7 100%);
    padding: 2rem 1rem;
  }

  .signup-shell {
    max-width: 700px;
    width: 100%;
  }

  .signup-header {
    text-align: center;
    margin-bottom: 2rem;
    color: white;
  }

  .eyebrow {
    text-transform: uppercase;
    letter-spacing: 0.2em;
    opacity: 0.85;
    font-size: 0.75rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .signup-header h1 {
    font-size: clamp(1.75rem, 4vw, 2.5rem);
    margin-bottom: 0.75rem;
    font-weight: 700;
    letter-spacing: -0.02em;
  }

  .signup-header .lede {
    opacity: 0.92;
    font-size: 1.05rem;
    line-height: 1.6;
    max-width: 500px;
    margin: 0 auto;
  }

  .error-banner {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.875rem 1rem;
    background: var(--color-error-bg);
    border: 1px solid #fecaca;
    color: var(--color-error);
    border-radius: 10px;
    margin-bottom: 1.5rem;
    font-weight: 500;
    font-size: 0.9rem;
    animation: shake 0.5s ease-in-out;
  }

  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    20%, 60% { transform: translateX(-5px); }
    40%, 80% { transform: translateX(5px); }
  }

  .error-banner span {
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 50%;
    background: var(--color-error);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 700;
    flex-shrink: 0;
  }

  /* Result Cards */
  .result-card {
    max-width: 500px;
    width: 100%;
    padding: 3rem 2rem;
    background: white;
    border-radius: 16px;
    text-align: center;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
    animation: fadeInUp 0.4s ease-out;
  }

  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .result-icon {
    width: 4.5rem;
    height: 4.5rem;
    margin: 0 auto 1.5rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: scaleIn 0.3s ease-out 0.2s both;
  }

  @keyframes scaleIn {
    from { transform: scale(0.8); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
  }

  .result-icon svg {
    width: 2.5rem;
    height: 2.5rem;
  }

  .result-card.success .result-icon {
    background: rgba(16, 185, 129, 0.12);
    color: #10b981;
  }

  .result-card.cancelled .result-icon {
    background: rgba(220, 38, 38, 0.12);
    color: #dc2626;
  }

  .result-card h1 {
    font-size: 1.75rem;
    color: var(--color-gray-800);
    margin-bottom: 1rem;
    font-weight: 700;
  }

  .result-card p {
    color: var(--color-gray-500);
    font-size: 1.05rem;
    line-height: 1.6;
    margin-bottom: 0.75rem;
  }

  .result-card .next-steps {
    color: var(--color-success);
    font-weight: 500;
    margin-bottom: 1.5rem;
  }

  .result-card .btn-primary,
  .result-card .btn-secondary {
    width: 100%;
    margin-bottom: 0.75rem;
  }

  .btn-primary {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.9rem 1.5rem;
    background: linear-gradient(135deg, #4A7DFF 0%, #6B5CE7 100%);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(74, 125, 255, 0.35);
  }

  .btn-secondary {
    padding: 0.9rem 1.5rem;
    background: white;
    color: var(--color-gray-700);
    border: 2px solid var(--color-gray-200);
    border-radius: 10px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-secondary:hover:not(:disabled) {
    border-color: #4A7DFF;
    color: #4A7DFF;
  }

  /* Dialog */
  .dialog-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    animation: fadeIn 0.2s ease-out;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .dialog-card {
    max-width: 420px;
    width: calc(100% - 2rem);
    background: white;
    border-radius: 16px;
    padding: 2rem;
    text-align: center;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
    animation: fadeInUp 0.3s ease-out;
  }

  .dialog-icon {
    width: 3.5rem;
    height: 3.5rem;
    margin: 0 auto 1.25rem;
    border-radius: 50%;
    background: rgba(245, 158, 11, 0.12);
    color: #f59e0b;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .dialog-icon svg {
    width: 2rem;
    height: 2rem;
  }

  .dialog-card h2 {
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--color-gray-800);
    margin-bottom: 0.5rem;
  }

  .dialog-card p {
    color: var(--color-gray-500);
    font-size: 0.95rem;
    line-height: 1.5;
    margin-bottom: 1.5rem;
  }

  .dialog-actions {
    display: flex;
    gap: 0.75rem;
  }

  .dialog-actions .btn-secondary,
  .dialog-actions .btn-primary {
    flex: 1;
  }
</style>
